import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"

import {
  getAccountOSAccountsTracker,
  getAccountOSActionsOfTheDay,
  getAccountOSExecutiveSummary,
  getAccountOSFinancialReports,
  getAccountOSJobOpenings,
  getAccountOSKeyIndustryTrends,
  getAccountOSOverview,
  getAccountOSPotentialPipelineIdentified,
  getAccountOSSuggestedNextSteps,
  getLatestGccAnnouncement,
  updateAccountOSActionOfTheDay
} from "~/api/accountos-overview"
import useGlobalStore from "~/store/global"
import { AccountOSActionsOfTheDayResponse } from "~/types/accountos-overview"
import { ITokenInfo } from "~/types/global"

export const useGetAccountOSExecutiveSummary = (
  account_id: string,
  duration: string
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const tenant = useGlobalStore((store) => store.tenant)
  const isOsLite =
    tenant?.tenant_type?.includes("os_lite") ||
    Boolean(tokenInfo?.is_os_lite_user)
  const isEnabled = !isOsLite
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: [
      "accountos-executive-sumamry",
      account_id,
      linkedTenantId,
      duration
    ],
    queryFn: async () => {
      const response = await getAccountOSExecutiveSummary(
        tokenInfo as ITokenInfo,
        account_id,
        linkedTenantId,
        duration
      )
      return response?.data?.data
    },
    enabled: isEnabled
  })
}

export const useGetAccountOSAccountsTracker = (
  account_id = "",
  duration: string,
  page = 1,
  limit = 10
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: [
      "accountos-accounts-tracker",
      account_id,
      duration,
      linkedTenantId,
      { page, limit }
    ],
    queryFn: async () => {
      const response = await getAccountOSAccountsTracker(
        tokenInfo as ITokenInfo,
        account_id,
        duration,
        linkedTenantId,
        page,
        limit
      )
      return response?.data?.data
    },
    placeholderData: (previousData) => previousData
  })
}

export const useGetAccountOSJobOpenings = (
  duration: string,
  account_id = ""
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: ["accountos-job-openings", account_id, linkedTenantId, duration],
    queryFn: async () => {
      const response = await getAccountOSJobOpenings(
        tokenInfo as ITokenInfo,
        account_id,
        duration,
        linkedTenantId
      )
      return response?.data?.data
    }
  })
}

export const useGetAccountOSPotentialPipelineIdentified = (
  duration: string,
  account_id = ""
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: [
      "accountos-potential-pipeline-identified",
      account_id,
      linkedTenantId,
      duration
    ],
    queryFn: async () => {
      const response = await getAccountOSPotentialPipelineIdentified(
        tokenInfo as ITokenInfo,
        account_id,
        duration,
        linkedTenantId
      )
      return response?.data?.data
    }
  })
}

export const useGetAccountOSOverview = <T = any>(
  duration: string,
  account_id = "",
  enabled = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: ["accountos-overview", account_id, linkedTenantId, duration],
    queryFn: async (): Promise<T> => {
      const response = await getAccountOSOverview(
        tokenInfo as ITokenInfo,
        account_id,
        duration,
        linkedTenantId
      )
      return response?.data?.data as T
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetAccountOSActionsOfTheDay = (
  duration: string,
  account_id = "",
  page = 1,
  limit = 10,
  enabled = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: [
      "accountos-actions-of-the-day",
      account_id,
      linkedTenantId,
      duration,
      page,
      limit
    ],
    queryFn: async (): Promise<AccountOSActionsOfTheDayResponse> => {
      const response = await getAccountOSActionsOfTheDay(
        tokenInfo as ITokenInfo,
        account_id,
        duration,
        page,
        limit,
        linkedTenantId
      )
      return response?.data?.data as AccountOSActionsOfTheDayResponse
    },
    enabled: enabled && !!tokenInfo,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always"
  })
}

export const useUpdateAccountOSActionOfTheDay = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useMutation({
    mutationFn: async (actionId: string | number) => {
      const response = await updateAccountOSActionOfTheDay(
        tokenInfo as ITokenInfo,
        actionId,
        linkedTenantId
      )
      return response?.data
    }
  })
}

export const useGetSuggestedNextSteps = (enabled = true) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: ["accountos-suggested-next-steps", linkedTenantId],
    queryFn: async () => {
      const response = await getAccountOSSuggestedNextSteps(
        tokenInfo as ITokenInfo,
        linkedTenantId
      )
      return response?.data?.data
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetAccountOSKeyIndustryTrends = (account_id = "") => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: ["accountos-key-industry-trends", account_id, linkedTenantId],
    queryFn: async () => {
      const response = await getAccountOSKeyIndustryTrends(
        tokenInfo as ITokenInfo,
        account_id,
        linkedTenantId
      )
      return response?.data?.data
    }
  })
}

export const useGetLatestGccAnnouncement = (enabled = true) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  return useQuery({
    queryKey: ["gcc-announcement-latest", linkedTenantId],
    queryFn: async () => {
      const response = await getLatestGccAnnouncement(
        tokenInfo as ITokenInfo,
        linkedTenantId
      )
      return response?.data?.data as { gcc_link?: string } | undefined
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetAccountOSFinancialReports = (
  account_ids: number[],
  duration: string,
  enabled = true,
  limit = 10
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: [
      "accountos-financial-reports",
      account_ids,
      linkedTenantId,
      duration,
      limit
    ],
    queryFn: async ({ pageParam }) => {
      const accountId = account_ids[pageParam.accountIndex]
      const response = await getAccountOSFinancialReports(
        tokenInfo as ITokenInfo,
        String(accountId),
        duration,
        pageParam.page,
        limit,
        linkedTenantId
      )
      return {
        ...response?.data?.data,
        accountIndex: pageParam.accountIndex
      }
    },
    initialPageParam: { accountIndex: 0, page: 1 },
    getNextPageParam: (lastPage: any) => {
      const total = lastPage?.total_reports ?? 0
      const currentPage = lastPage?.page ?? 1
      const pageLimit = lastPage?.limit ?? limit
      const loadedSoFar = currentPage * pageLimit
      if (loadedSoFar < total) {
        return { accountIndex: lastPage.accountIndex, page: currentPage + 1 }
      }
      if (lastPage.accountIndex + 1 < account_ids.length) {
        return { accountIndex: lastPage.accountIndex + 1, page: 1 }
      }
      return undefined
    },
    enabled: enabled && !!tokenInfo && account_ids.length > 0,
    refetchOnWindowFocus: false
  })

  const reports =
    data?.pages.flatMap((page: any) => page?.financial_reports ?? []) ?? []
  const totalReports = (() => {
    const seen = new Set<number>()
    let sum = 0
    for (const page of (data?.pages ?? []) as any[]) {
      if (!seen.has(page.accountIndex)) {
        seen.add(page.accountIndex)
        sum += page.total_reports ?? 0
      }
    }
    return sum
  })()

  return {
    reports,
    totalReports,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch
  }
}
